import React, { useState, useEffect, useCallback } from 'react';
import { Home } from 'lucide-react';
import sendCustomerComplaintEmail from './customerComplaintEmailService';
import PVSPageLayout from './PVSPageLayout';
import FormSuccessModal from './FormSuccessModal';
import { getCurrentUserDisplayName } from '../utils/currentUser';
import { lookupVesselBySerial } from './formTraceabilityService';

const MISSING_PLACEHOLDER = 'Unable to obtain — please enter';

const isCurrentUserAdmin = () => {
  try {
    const raw = localStorage.getItem('user');
    if (!raw) return false;
    const user = JSON.parse(raw);
    return user?.isAdmin === true;
  } catch (_) {
    return false;
  }
};

const toDateInput = (value) => {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  return d.toISOString().split('T')[0];
};

const initialFormData = () => ({
  dateRaised: new Date().toISOString().split('T')[0],
  raisedBy: '',
  clientName: '',
  description: '',
  serialNumber: '',
  preInspectionDate: '',
  preInspectionBy: '',
  datePainted: '',
  paintedBy: '',
  inspectionDate: '',
  inspectionBy: '',
  qualityControlDate: '',
  qualityControlBy: '',
  dateDispatched: '',
  waterCapacity: '',
  vesselOwner: '',
  pvsInspectorName: '',
  requiresEscalation: 'no',
  dateEscalated: '',
  clientRepresentative: '',
  contactMethod: '',
  affectsOtherVessels: 'no',
  previousCompromised: 'no',
  riskLevel: '',
  safetyImpact: 'no',
  safetyImpactDetails: '',
  impactSummary: '',
  rootCause: '',
  correctiveAction: '',
  preventativeAction: '',
  qualityManagerSignoff: '',
  qualityManagerDate: '',
  clientSignoff: '',
  clientSignoffDate: ''
});

const CustomerComplaintForm = ({ onBack }) => {
  const [isAdmin] = useState(() => isCurrentUserAdmin());
  const [formData, setFormData] = useState(initialFormData());
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState('');
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [serialLookupState, setSerialLookupState] = useState({ loading: false, notFound: false, lastLookup: '' });
  const [missingVesselFields, setMissingVesselFields] = useState({});

  useEffect(() => {
    const name = getCurrentUserDisplayName();
    if (!name) return;
    setFormData(prev => (prev.raisedBy ? prev : { ...prev, raisedBy: name }));
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (name === 'serialNumber' && serialLookupState.lastLookup) {
      setSerialLookupState({ loading: false, notFound: false, lastLookup: '' });
      setMissingVesselFields({});
    }
  };

  const handleSerialLookup = useCallback(async () => {
    const serial = String(formData.serialNumber || '').trim();
    if (!serial || serial === serialLookupState.lastLookup) return;

    setSerialLookupState({ loading: true, notFound: false, lastLookup: serial });
    try {
      const result = await lookupVesselBySerial(serial);
      if (!result.found) {
        setSerialLookupState({ loading: false, notFound: true, lastLookup: serial });
        setMissingVesselFields({});
        return;
      }

      const vessel = result.vessel || {};
      const formProgress = result.formProgress || {};

      const preInspectionDate = toDateInput(
        formProgress?.preInspection?.completedAt || vessel.dateReceived
      );
      const preInspectionBy = formProgress?.preInspection?.completedBy || '';
      const datePainted = toDateInput(
        formProgress?.paintQualityInspection?.completedAt || vessel.dateRefurbed
      );
      const paintedBy = formProgress?.paintQualityInspection?.completedBy || '';
      const inspectionDate = toDateInput(
        formProgress?.tankCertification?.completedAt
      );
      const inspectionBy = formProgress?.tankCertification?.completedBy || '';
      const qualityControlDate = toDateInput(
        formProgress?.finalQCChecklist?.completedAt
      );
      const qualityControlBy = formProgress?.finalQCChecklist?.completedBy || '';
      const dateDispatched = toDateInput(vessel.dateReturned);
      const waterCapacity = (vessel.vesselSize !== null && vessel.vesselSize !== undefined && vessel.vesselSize !== '')
        ? String(vessel.vesselSize)
        : '';
      const vesselOwner = vessel.owner || '';

      const missing = {};
      if (!preInspectionDate) missing.preInspectionDate = true;
      if (!preInspectionBy) missing.preInspectionBy = true;
      if (!datePainted) missing.datePainted = true;
      if (!paintedBy) missing.paintedBy = true;
      if (!inspectionDate) missing.inspectionDate = true;
      if (!inspectionBy) missing.inspectionBy = true;
      if (!qualityControlDate) missing.qualityControlDate = true;
      if (!qualityControlBy) missing.qualityControlBy = true;
      if (!dateDispatched) missing.dateDispatched = true;
      if (!waterCapacity) missing.waterCapacity = true;
      if (!vesselOwner) missing.vesselOwner = true;

      setMissingVesselFields(missing);
      setSerialLookupState({ loading: false, notFound: false, lastLookup: serial });

      setFormData(prev => ({
        ...prev,
        preInspectionDate: prev.preInspectionDate || preInspectionDate,
        preInspectionBy: prev.preInspectionBy || preInspectionBy,
        datePainted: prev.datePainted || datePainted,
        paintedBy: prev.paintedBy || paintedBy,
        inspectionDate: prev.inspectionDate || inspectionDate,
        inspectionBy: prev.inspectionBy || inspectionBy,
        qualityControlDate: prev.qualityControlDate || qualityControlDate,
        qualityControlBy: prev.qualityControlBy || qualityControlBy,
        dateDispatched: prev.dateDispatched || dateDispatched,
        waterCapacity: prev.waterCapacity || waterCapacity,
        vesselOwner: prev.vesselOwner || vesselOwner
      }));
    } catch (error) {
      console.error('Serial lookup failed:', error);
      setSerialLookupState({ loading: false, notFound: false, lastLookup: serial });
      setMissingVesselFields({});
    }
  }, [formData.serialNumber, serialLookupState.lastLookup]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitMessage('');

    if (!formData.description.trim()) {
      setSubmitMessage('Please provide a description of the complaint.');
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }
    if (!formData.rootCause.trim()) {
      setSubmitMessage('Please provide the root cause identified.');
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }
    if (!formData.correctiveAction.trim() || !formData.preventativeAction.trim()) {
      setSubmitMessage('Please provide both corrective and preventative actions.');
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }
    if (formData.requiresEscalation === 'yes' && (!formData.dateEscalated || !formData.clientRepresentative.trim())) {
      setSubmitMessage('Please complete escalation date and client representative when escalation is required.');
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }
    if (!formData.riskLevel) {
      setSubmitMessage('Please select a risk level (Low, Medium, or High).');
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }
    if (formData.safetyImpact === 'yes' && !formData.safetyImpactDetails.trim()) {
      setSubmitMessage('Please describe the safety impact.');
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    setIsSubmitting(true);
    try {
      await sendCustomerComplaintEmail(formData);
      setFormData(initialFormData());
      setShowSuccessModal(true);
    } catch (error) {
      console.error('Error submitting customer complaint form:', error);
      setSubmitMessage(error.message || 'Error submitting form. Please try again.');
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const YesNoGroup = ({ name, value }) => (
    <div className="flex gap-4">
      {['yes', 'no'].map(opt => (
        <label key={opt} className="inline-flex items-center gap-2 text-pvs-text-muted">
          <input
            type="radio"
            name={name}
            value={opt}
            checked={value === opt}
            onChange={handleInputChange}
            className="accent-pvs-accent"
          />
          <span className="capitalize">{opt}</span>
        </label>
      ))}
    </div>
  );

  if (!isAdmin) {
    return (
      <PVSPageLayout className="py-12 px-4">
        <div className="max-w-2xl mx-auto">
          <div className="pvs-glass rounded-xl shadow-pvs-glow border border-red-500/40 p-8 text-center">
            <h1 className="text-2xl font-bold text-red-400 mb-3">Access Restricted</h1>
            <p className="text-pvs-text-muted mb-6">
              The Customer Complaint & Appeal Form is for admin use only. Please contact a PVS administrator if you need to raise a complaint or appeal.
            </p>
            <button
              type="button"
              onClick={onBack}
              className="px-6 py-3 rounded-lg pvs-glass pvs-glass-hover text-white transition-all duration-200"
            >
              Back to Forms
            </button>
          </div>
        </div>
      </PVSPageLayout>
    );
  }

  return (
    <PVSPageLayout className="py-12 px-4">
      <div className="max-w-4xl mx-auto">
        <div className="pvs-glass rounded-xl shadow-pvs-glow border border-pvs-accent/30">
          <div className="border-b border-pvs-border p-6">
            <div className="flex justify-between items-center mb-4">
              <div>
                <h1 className="text-3xl font-bold text-white">Customer Complaint & Appeal Form</h1>
                <p className="text-xs text-pvs-text-dim mt-1">Ref: PVS-CCF-001 | Version: 1.0</p>
              </div>
              <button
                type="button"
                onClick={onBack}
                className="flex items-center justify-center w-12 h-12 flex-shrink-0 rounded-xl pvs-glass pvs-glass-hover transition-all duration-200"
                title="Return to Forms"
              >
                <Home className="w-6 h-6" stroke="url(#icon-two-tone)" />
              </button>
            </div>
            <p className="text-pvs-text-dim text-center">Customer Complaint & Appeal Documentation</p>
            <div className="mt-4 p-3 rounded-lg border border-amber-500/40 bg-amber-900/20 text-center">
              <p className="text-sm font-bold tracking-wider text-amber-300 uppercase">FOR INTERNAL USE ONLY</p>
              <p className="text-xs text-amber-200/80 mt-1 uppercase">Not for distribution outside PVS Limited without authorisation.</p>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="p-6 space-y-6">
            {submitMessage && (
              <div className="p-5 rounded-lg border-2 shadow-md bg-red-900/30 text-red-400 border-red-500/30">
                <p className="text-sm font-medium">{submitMessage}</p>
              </div>
            )}

            {/* General Information */}
            <div className="border-b border-pvs-border pb-6">
              <h2 className="text-xl font-semibold text-pvs-accent mb-4">General Information</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-pvs-text-muted mb-2">Date Raised *</label>
                  <input type="date" name="dateRaised" value={formData.dateRaised} onChange={handleInputChange} className="block w-full pvs-input" required />
                </div>
                <div>
                  <label className="block text-sm font-medium text-pvs-text-muted mb-2">Raised By (Name / Role) *</label>
                  <input type="text" name="raisedBy" value={formData.raisedBy} onChange={handleInputChange} className="block w-full pvs-input" required />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-pvs-text-muted mb-2">Client / Customer Name *</label>
                  <input type="text" name="clientName" value={formData.clientName} onChange={handleInputChange} className="block w-full pvs-input" required />
                </div>
              </div>
            </div>

            {/* Section 1 */}
            <div className="border-b border-pvs-border pb-6">
              <h2 className="text-xl font-semibold text-pvs-accent mb-4">Section 1: Complaint / Appeal Details</h2>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-pvs-text-muted mb-2">Description of Complaint *</label>
                  <textarea name="description" value={formData.description} onChange={handleInputChange} rows={5} className="block w-full pvs-input" required />
                </div>
                <p className="text-sm text-pvs-text-dim">Related Asset / Vessel Details (if applicable)</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-pvs-text-muted mb-2">Serial Number</label>
                    <input
                      type="text"
                      name="serialNumber"
                      value={formData.serialNumber}
                      onChange={handleInputChange}
                      onBlur={handleSerialLookup}
                      className="block w-full pvs-input"
                    />
                    {serialLookupState.loading && (
                      <p className="mt-1 text-xs text-pvs-text-dim">Looking up vessel…</p>
                    )}
                    {!serialLookupState.loading && serialLookupState.notFound && (
                      <p className="mt-1 text-xs text-red-400">Serial number not found in Vessel Inventory.</p>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-pvs-text-muted mb-2">PVS Inspector / Operator</label>
                    <input type="text" name="pvsInspectorName" value={formData.pvsInspectorName} onChange={handleInputChange} className="block w-full pvs-input" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-pvs-text-muted mb-2">Vessel Owner</label>
                    <input
                      type="text"
                      name="vesselOwner"
                      value={formData.vesselOwner}
                      onChange={handleInputChange}
                      placeholder={missingVesselFields.vesselOwner ? MISSING_PLACEHOLDER : ''}
                      className="block w-full pvs-input"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-pvs-text-muted mb-2">Water Capacity</label>
                    <input
                      type="text"
                      name="waterCapacity"
                      value={formData.waterCapacity}
                      onChange={handleInputChange}
                      placeholder={missingVesselFields.waterCapacity ? MISSING_PLACEHOLDER : ''}
                      className="block w-full pvs-input"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-pvs-text-muted mb-2">Pre Inspection Date</label>
                    <input
                      type="date"
                      name="preInspectionDate"
                      value={formData.preInspectionDate}
                      onChange={handleInputChange}
                      className="block w-full pvs-input"
                    />
                    {missingVesselFields.preInspectionDate && (
                      <p className="mt-1 text-xs text-amber-400">{MISSING_PLACEHOLDER}</p>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-pvs-text-muted mb-2">Pre Inspection Completed By</label>
                    <input
                      type="text"
                      name="preInspectionBy"
                      value={formData.preInspectionBy}
                      onChange={handleInputChange}
                      placeholder={missingVesselFields.preInspectionBy ? MISSING_PLACEHOLDER : ''}
                      className="block w-full pvs-input"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-pvs-text-muted mb-2">Date Painted</label>
                    <input
                      type="date"
                      name="datePainted"
                      value={formData.datePainted}
                      onChange={handleInputChange}
                      className="block w-full pvs-input"
                    />
                    {missingVesselFields.datePainted && (
                      <p className="mt-1 text-xs text-amber-400">{MISSING_PLACEHOLDER}</p>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-pvs-text-muted mb-2">Paint QC Completed By</label>
                    <input
                      type="text"
                      name="paintedBy"
                      value={formData.paintedBy}
                      onChange={handleInputChange}
                      placeholder={missingVesselFields.paintedBy ? MISSING_PLACEHOLDER : ''}
                      className="block w-full pvs-input"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-pvs-text-muted mb-2">Inspection Date</label>
                    <input
                      type="date"
                      name="inspectionDate"
                      value={formData.inspectionDate}
                      onChange={handleInputChange}
                      className="block w-full pvs-input"
                    />
                    {missingVesselFields.inspectionDate && (
                      <p className="mt-1 text-xs text-amber-400">{MISSING_PLACEHOLDER}</p>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-pvs-text-muted mb-2">Inspection Completed By</label>
                    <input
                      type="text"
                      name="inspectionBy"
                      value={formData.inspectionBy}
                      onChange={handleInputChange}
                      placeholder={missingVesselFields.inspectionBy ? MISSING_PLACEHOLDER : ''}
                      className="block w-full pvs-input"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-pvs-text-muted mb-2">Quality Control Date</label>
                    <input
                      type="date"
                      name="qualityControlDate"
                      value={formData.qualityControlDate}
                      onChange={handleInputChange}
                      className="block w-full pvs-input"
                    />
                    {missingVesselFields.qualityControlDate && (
                      <p className="mt-1 text-xs text-amber-400">{MISSING_PLACEHOLDER}</p>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-pvs-text-muted mb-2">QC Completed By</label>
                    <input
                      type="text"
                      name="qualityControlBy"
                      value={formData.qualityControlBy}
                      onChange={handleInputChange}
                      placeholder={missingVesselFields.qualityControlBy ? MISSING_PLACEHOLDER : ''}
                      className="block w-full pvs-input"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-pvs-text-muted mb-2">Date Dispatched</label>
                    <input
                      type="date"
                      name="dateDispatched"
                      value={formData.dateDispatched}
                      onChange={handleInputChange}
                      className="block w-full pvs-input"
                    />
                    {missingVesselFields.dateDispatched && (
                      <p className="mt-1 text-xs text-amber-400">{MISSING_PLACEHOLDER}</p>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Section 2 */}
            <div className="border-b border-pvs-border pb-6">
              <h2 className="text-xl font-semibold text-pvs-accent mb-4">Section 2: Client Escalation</h2>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-pvs-text-muted mb-2">Does this complaint require formal escalation to the client?</label>
                  <YesNoGroup name="requiresEscalation" value={formData.requiresEscalation} />
                </div>
                {formData.requiresEscalation === 'yes' && (
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-pvs-text-muted mb-2">Date Escalated *</label>
                      <input type="date" name="dateEscalated" value={formData.dateEscalated} onChange={handleInputChange} className="block w-full pvs-input" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-pvs-text-muted mb-2">Client Representative Contacted *</label>
                      <input type="text" name="clientRepresentative" value={formData.clientRepresentative} onChange={handleInputChange} className="block w-full pvs-input" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-pvs-text-muted mb-2">Method of Contact</label>
                      <input type="text" name="contactMethod" value={formData.contactMethod} onChange={handleInputChange} placeholder="e.g., Email, Phone" className="block w-full pvs-input" />
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Section 3 */}
            <div className="border-b border-pvs-border pb-6">
              <h2 className="text-xl font-semibold text-pvs-accent mb-4">Section 3: Extent and Impact Assessment</h2>
              <p className="text-xs text-pvs-text-dim mb-4">As required by ISO 17020.</p>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-pvs-text-muted mb-2">Does this affect other assets / vessels in the current batch?</label>
                  <YesNoGroup name="affectsOtherVessels" value={formData.affectsOtherVessels} />
                </div>
                <div>
                  <label className="block text-sm font-medium text-pvs-text-muted mb-2">Are previous inspections or services potentially compromised?</label>
                  <YesNoGroup name="previousCompromised" value={formData.previousCompromised} />
                </div>
                <div>
                  <label className="block text-sm font-medium text-pvs-text-muted mb-2">Risk Level *</label>
                  <div className="flex flex-wrap gap-4">
                    {[
                      { value: 'Low', color: 'text-green-400', ring: 'focus:ring-green-500' },
                      { value: 'Medium', color: 'text-amber-400', ring: 'focus:ring-amber-500' },
                      { value: 'High', color: 'text-red-400', ring: 'focus:ring-red-500' }
                    ].map(opt => (
                      <label key={opt.value} className="inline-flex items-center gap-2 text-pvs-text-muted">
                        <input
                          type="radio"
                          name="riskLevel"
                          value={opt.value}
                          checked={formData.riskLevel === opt.value}
                          onChange={handleInputChange}
                          className={`accent-pvs-accent ${opt.ring}`}
                        />
                        <span className={`font-semibold ${opt.color}`}>{opt.value}</span>
                      </label>
                    ))}
                  </div>
                  <p className="mt-1 text-xs text-pvs-text-dim">Low: minor / isolated. Medium: multiple assets or repeat issue. High: safety, regulatory, or widespread impact.</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-pvs-text-muted mb-2">Is there a safety impact? *</label>
                  <YesNoGroup name="safetyImpact" value={formData.safetyImpact} />
                </div>
                {formData.safetyImpact === 'yes' && (
                  <div className="p-4 rounded-lg border border-red-500/40 bg-red-900/20">
                    <label className="block text-sm font-medium text-red-300 mb-2">Describe the Safety Impact *</label>
                    <textarea name="safetyImpactDetails" value={formData.safetyImpactDetails} onChange={handleInputChange} rows={3} className="block w-full pvs-input" required />
                  </div>
                )}
                <div>
                  <label className="block text-sm font-medium text-pvs-text-muted mb-2">Impact Summary</label>
                  <textarea name="impactSummary" value={formData.impactSummary} onChange={handleInputChange} rows={4} className="block w-full pvs-input" />
                </div>
              </div>
            </div>

            {/* Section 4 */}
            <div className="border-b border-pvs-border pb-6">
              <h2 className="text-xl font-semibold text-pvs-accent mb-4">Section 4: Root Cause Investigation</h2>
              <div>
                <label className="block text-sm font-medium text-pvs-text-muted mb-2">Root Cause Identified *</label>
                <textarea name="rootCause" value={formData.rootCause} onChange={handleInputChange} rows={4} className="block w-full pvs-input" required />
              </div>
            </div>

            {/* Section 5 */}
            <div className="border-b border-pvs-border pb-6">
              <h2 className="text-xl font-semibold text-pvs-accent mb-4">Section 5: Corrective and Preventative Action</h2>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-pvs-text-muted mb-2">Immediate Corrective Action Taken *</label>
                  <textarea name="correctiveAction" value={formData.correctiveAction} onChange={handleInputChange} rows={4} className="block w-full pvs-input" required />
                </div>
                <div>
                  <label className="block text-sm font-medium text-pvs-text-muted mb-2">Preventative Action Implemented *</label>
                  <textarea name="preventativeAction" value={formData.preventativeAction} onChange={handleInputChange} rows={4} className="block w-full pvs-input" required />
                </div>
              </div>
            </div>

            {/* Section 6 */}
            <div className="pb-2">
              <h2 className="text-xl font-semibold text-pvs-accent mb-4">Section 6: Close-Out & Signatures</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-pvs-text-muted mb-2">PVS Quality / Operations Manager Sign-off</label>
                  <input type="text" name="qualityManagerSignoff" value={formData.qualityManagerSignoff} onChange={handleInputChange} className="block w-full pvs-input" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-pvs-text-muted mb-2">Sign-off Date</label>
                  <input type="date" name="qualityManagerDate" value={formData.qualityManagerDate} onChange={handleInputChange} className="block w-full pvs-input" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-pvs-text-muted mb-2">Client Representative Sign-off (if required)</label>
                  <input type="text" name="clientSignoff" value={formData.clientSignoff} onChange={handleInputChange} className="block w-full pvs-input" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-pvs-text-muted mb-2">Client Sign-off Date</label>
                  <input type="date" name="clientSignoffDate" value={formData.clientSignoffDate} onChange={handleInputChange} className="block w-full pvs-input" />
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-3 pt-4">
              <button
                type="button"
                onClick={onBack}
                className="px-6 py-3 rounded-lg pvs-glass pvs-glass-hover text-white transition-all duration-200"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isSubmitting}
                className="px-8 py-3 bg-pvs-accent text-pvs-bg font-semibold rounded-lg shadow-lg hover:bg-pvs-accent-dim transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-pvs-accent/50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? 'Submitting…' : 'Submit Form'}
              </button>
            </div>
          </form>
        </div>
      </div>

      <FormSuccessModal
        isOpen={showSuccessModal}
        formName="Customer Complaint & Appeal Form"
        onClose={() => { setShowSuccessModal(false); onBack(); }}
      />
    </PVSPageLayout>
  );
};

export default CustomerComplaintForm;
